import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
    Activity, 
    Upload, 
    CheckCircle, 
    Trash2, 
    RefreshCw, 
    FileText,
    XCircle,
    Filter,
    Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SortableHeader, sortData } from '../components/SortableHeader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const ActivityLog = () => {
    const { user } = useAuth();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all');
    const [sortKey, setSortKey] = useState('timestamp');
    const [sortDirection, setSortDirection] = useState('desc');
    const API_URL = '';
    
    // Handle sorting
    const handleSort = (key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    };
    
    // Pull "Email recibido: dd/mm/yyyy hh:mm AM" out of the note into its own column
    const splitEmailDate = (note) => {
        const m = /\s*-\s*Email recibido:\s*(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(note || '');
        if (!m) return { note_clean: note || '', email_date: null, email_date_sort: '' };
        const [, dd, mm, yyyy, hh, min, ap] = m;
        let h = parseInt(hh, 10) % 12;
        if (ap.toUpperCase() === 'PM') h += 12;
        return {
            note_clean: (note || '').replace(m[0], '').trim(),
            email_date: `${dd}/${mm}/${yyyy} ${hh}:${min} ${ap.toUpperCase()}`,
            email_date_sort: `${yyyy}-${mm}-${dd}T${String(h).padStart(2, '0')}:${min}`,
        };
    };

    // Sort activities
    const sortedActivities = useMemo(() => {
        const enriched = activities.map((a) => ({ ...a, ...splitEmailDate(a.note) }));
        return sortData(enriched, sortKey, sortDirection);
    }, [activities, sortKey, sortDirection]);

    const fetchActivities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            let url = `${API_URL}/api/activity-log`;
            if (filterType && filterType !== 'all') {
                url += `?activity_type=${filterType}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setActivities(data);
            } else {
                toast.error('Error al cargar el historial');
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
            toast.error('Error de conexión');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, [filterType]);

    const handleClearHistory = async () => {
        if (!window.confirm('¿Está seguro de que desea eliminar todo el historial de actividades?')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/activity-log`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Historial eliminado correctamente');
                setActivities([]);
            } else {
                toast.error('Error al eliminar el historial');
            }
        } catch (error) {
            console.error('Error clearing history:', error);
            toast.error('Error de conexión');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'factura_subida':
                return <Upload className="w-4 h-4" />;
            case 'factura_confirmada':
                return <CheckCircle className="w-4 h-4" />;
            case 'factura_desconfirmada':
            case 'confirmacion_removida':
                return <XCircle className="w-4 h-4" />;
            case 'factura_eliminada':
                return <Trash2 className="w-4 h-4" />;
            default:
                return <Activity className="w-4 h-4" />;
        }
    };

    const getActivityLabel = (type) => {
        switch (type) {
            case 'factura_subida':
                return 'Subida';
            case 'factura_confirmada':
                return 'Confirmada';
            case 'factura_desconfirmada':
                return 'Desconfirmada';
            case 'confirmacion_removida':
                return 'Confirmación removida';
            case 'factura_eliminada':
                return 'Eliminada';
            default:
                return type;
        }
    };

    const getActivityColor = (type) => {
        switch (type) {
            case 'factura_subida':
                return 'bg-blue-100 text-blue-700';
            case 'factura_confirmada':
                return 'bg-green-100 text-green-700';
            case 'factura_desconfirmada':
                return 'bg-yellow-100 text-yellow-700';
            case 'confirmacion_removida':
                return 'bg-orange-100 text-orange-700';
            case 'factura_eliminada':
                return 'bg-red-100 text-red-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <div className="space-y-6" data-testid="activity-log-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <Activity className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Registro de Actividades</h1>
                        <p className="text-slate-500 text-sm">Historial completo de facturas del sistema</p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-[180px]" data-testid="activity-filter">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Filtrar por tipo" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas las actividades</SelectItem>
                            <SelectItem value="factura_subida">Facturas subidas</SelectItem>
                            <SelectItem value="factura_confirmada">Facturas confirmadas</SelectItem>
                            <SelectItem value="factura_desconfirmada">Facturas desconfirmadas</SelectItem>
                            <SelectItem value="factura_eliminada">Facturas eliminadas</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        onClick={fetchActivities}
                        className="flex items-center gap-2"
                        data-testid="refresh-activity-btn"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Actualizar
                    </Button>
                    {user?.role === 'admin' && activities.length > 0 && (
                        <Button
                            variant="destructive"
                            onClick={handleClearHistory}
                            className="flex items-center gap-2"
                            data-testid="clear-activity-btn"
                        >
                            <Trash2 className="w-4 h-4" />
                            Limpiar
                        </Button>
                    )}
                </div>
            </div>

            {/* Activities List */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="spinner mx-auto mb-4"></div>
                        <p className="text-slate-500">Cargando actividades...</p>
                    </div>
                ) : activities.length === 0 ? (
                    <div className="p-8 text-center">
                        <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No hay actividades registradas</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="activity-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <SortableHeader 
                                        label="Fecha" 
                                        sortKey="created_at" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Actividad" 
                                        sortKey="activity_type" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Factura" 
                                        sortKey="invoice_name" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Usuario Factura" 
                                        sortKey="invoice_user_name" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Monto" 
                                        sortKey="amount" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Realizado por" 
                                        sortKey="user_name" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <SortableHeader 
                                        label="Fecha email" 
                                        sortKey="email_date_sort" 
                                        currentSort={sortKey} 
                                        currentDirection={sortDirection} 
                                        onSort={handleSort}
                                        className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider"
                                    />
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                                        Nota
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {sortedActivities.map((activity) => (
                                    <tr 
                                        key={activity.id} 
                                        className="hover:bg-slate-50 transition-colors"
                                        data-testid={`activity-row-${activity.id}`}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600">
                                            {formatDate(activity.created_at)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getActivityColor(activity.activity_type)}`}>
                                                {getActivityIcon(activity.activity_type)}
                                                {getActivityLabel(activity.activity_type)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="text-sm font-medium text-slate-800">
                                                {activity.invoice_name || '-'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="text-sm text-slate-600">
                                                {activity.invoice_user_name || '-'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-slate-800 text-right">
                                            {formatAmount(activity.amount)}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className="text-sm text-slate-600">
                                                {activity.user_name}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-600" data-testid={`activity-email-date-${activity.id}`}>
                                            {activity.email_date ? (
                                                <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-sky-500" />{activity.email_date}</span>
                                            ) : (
                                                <span className="text-slate-300">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-slate-600 min-w-[280px]">
                                            {activity.note_clean ? (
                                                <span className="flex items-start gap-1.5">
                                                    <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                                                    <span className="break-words whitespace-normal">
                                                        {activity.note_clean}
                                                    </span>
                                                </span>
                                            ) : (
                                                <span className="text-slate-300 italic">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Summary */}
            {!loading && activities.length > 0 && (
                <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
                    <p className="text-sm text-indigo-700">
                        <span className="font-semibold">{activities.length}</span> actividades registradas
                        {filterType !== 'all' && ` (filtrado por: ${getActivityLabel(filterType)})`}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ActivityLog;
